import {
  type CSSProperties,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTheme, type SubjectColorKey } from "@repo/tokens";
import {
  getTopicSummaryGridConfigForScreenWidth,
  getResponsiveGridLayout,
} from "../../../../../lib/gridUtils";
import { TopicSummaryWidgetShimmer } from "./TopicSummaryWidgetShimmer";

const TABLET_MIN_WIDTH = 600;
const DESKTOP_MIN_WIDTH = 1024;
const TOPIC_SUMMARY_TABLET_FIT_MIN_CARD_WIDTH = 200;

const SHIMMER_ROW_MULTIPLIER = 4;

export const TopicSummaryShimmerGrid = ({
  screenWidth,
  subjectName,
  className,
  style,
  minColumns = 2,
  applyHorizontalPadding = false,
}: {
  screenWidth: number;
  subjectName: string;
  className?: string;
  style?: CSSProperties;
  minColumns?: number;
  applyHorizontalPadding?: boolean;
}) => {
  const { theme } = useTheme();
  const { minCardWidth, columnGap, horizontalPadding } =
    getTopicSummaryGridConfigForScreenWidth(screenWidth);
  const hostRef = useRef<HTMLDivElement>(null);
  const [contentWidth, setContentWidth] = useState(0);

  useLayoutEffect(() => {
    const el = hostRef.current;
    if (!el) return;

    const read = () => {
      const cs = getComputedStyle(el);
      const horizontalPad =
        parseFloat(cs.paddingLeft) + parseFloat(cs.paddingRight);
      const innerWidth = Math.max(0, el.clientWidth - horizontalPad);
      setContentWidth(innerWidth);
    };

    read();
    const ro = new ResizeObserver(read);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const paddingX = applyHorizontalPadding ? horizontalPadding : 0;
  const fallbackContentWidth = Math.max(0, screenWidth - paddingX * 2);
  const fitMinCardWidth =
    screenWidth >= TABLET_MIN_WIDTH && screenWidth < DESKTOP_MIN_WIDTH
      ? Math.min(minCardWidth, TOPIC_SUMMARY_TABLET_FIT_MIN_CARD_WIDTH)
      : minCardWidth;

  const layout = useMemo(
    () =>
      getResponsiveGridLayout({
        containerContentWidth:
          contentWidth > 0 ? contentWidth : fallbackContentWidth,
        minCardWidth,
        columnGap,
        horizontalPadding,
        fitMinCardWidth,
        minColumns,
      }),
    [
      contentWidth,
      fallbackContentWidth,
      minCardWidth,
      columnGap,
      horizontalPadding,
      fitMinCardWidth,
      minColumns,
    ],
  );

  const subjectColor =
    theme.colors.subject[subjectName as SubjectColorKey] ??
    theme.colors.primary;
  const baseColor = subjectColor[95];
  const highlightColor = subjectColor[100];

  const hostStyle = useMemo<CSSProperties>(
    () => ({
      paddingLeft: paddingX,
      paddingRight: paddingX,
      width: "100%",
      boxSizing: "border-box",
      ...style,
    }),
    [paddingX, style],
  );

  const gridStyle = useMemo<CSSProperties>(
    () => ({
      display: "grid",
      gridTemplateColumns: `repeat(${layout.columns}, minmax(0, 1fr))`,
      columnGap: layout.columnGap,
      rowGap: layout.columnGap,
      width: "100%",
      boxSizing: "border-box",
    }),
    [layout.columnGap, layout.columns],
  );

  const shimmerCount = layout.columns * SHIMMER_ROW_MULTIPLIER;

  return (
    <div ref={hostRef} style={hostStyle}>
      <div className={className} style={gridStyle}>
        {Array.from({ length: shimmerCount }, (_, i) => (
          <TopicSummaryWidgetShimmer
            key={i}
            baseColor={baseColor}
            highlightColor={highlightColor}
            cardWidth={layout.cardWidth}
          />
        ))}
      </div>
    </div>
  );
};
